import { foldForComparison } from '../text';

/** ISO 3166-1 alpha-2 codes (plus XK). Names are resolved with Intl.DisplayNames. */
const ISO_CODES =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'.split(
    ' ',
  );

const FALLBACK_NAMES: Record<string, string> = {
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  EG: 'Egypt',
  KW: 'Kuwait',
  QA: 'Qatar',
  BH: 'Bahrain',
  OM: 'Oman',
  JO: 'Jordan',
  LB: 'Lebanon',
  MA: 'Morocco',
  GB: 'United Kingdom',
  US: 'United States',
};

const COUNTRY_ALIASES: Record<string, string> = {
  uae: 'AE',
  'u.a.e': 'AE',
  'u.a.e.': 'AE',
  emirates: 'AE',
  'united arab emirates': 'AE',
  'الامارات': 'AE',
  'الامارات العربيه المتحده': 'AE',
  ksa: 'SA',
  'k.s.a': 'SA',
  saudi: 'SA',
  'saudi arabia': 'SA',
  'kingdom of saudi arabia': 'SA',
  'السعوديه': 'SA',
  'المملكه العربيه السعوديه': 'SA',
  egypt: 'EG',
  'arab republic of egypt': 'EG',
  'مصر': 'EG',
  'جمهوريه مصر العربيه': 'EG',
  kuwait: 'KW',
  'الكويت': 'KW',
  qatar: 'QA',
  'قطر': 'QA',
  bahrain: 'BH',
  'البحرين': 'BH',
  oman: 'OM',
  'عمان': 'OM',
  jordan: 'JO',
  'الاردن': 'JO',
  lebanon: 'LB',
  'لبنان': 'LB',
  morocco: 'MA',
  'المغرب': 'MA',
  uk: 'GB',
  'united kingdom': 'GB',
  'great britain': 'GB',
  england: 'GB',
  usa: 'US',
  'u.s.a': 'US',
  'united states': 'US',
  'united states of america': 'US',
};

export interface CountryInfo {
  code: string;
  name: string;
}

let countryCache: CountryInfo[] | null = null;
let countryIndex: Map<string, string> | null = null;

function displayNames(): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
}

export function listCountries(): CountryInfo[] {
  if (countryCache) return countryCache;
  const names = displayNames();
  countryCache = ISO_CODES.map((code) => {
    let name = FALLBACK_NAMES[code] ?? code;
    try {
      name = names?.of(code) ?? name;
    } catch {
      // keep fallback
    }
    return { code, name };
  }).sort((a, b) => a.name.localeCompare(b.name));
  return countryCache;
}

export function countryName(code: string | null | undefined): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  return listCountries().find((country) => country.code === upper)?.name ?? upper;
}

/** Resolve free text ("KSA", "الإمارات", "Egypt", "eg") to an ISO alpha-2 code. */
export function normalizeCountry(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed) && ISO_CODES.includes(trimmed.toUpperCase())) return trimmed.toUpperCase();
  const folded = foldForComparison(trimmed).replace(/[.]+$/, '');
  const alias = COUNTRY_ALIASES[folded];
  if (alias) return alias;
  if (!countryIndex) {
    countryIndex = new Map(listCountries().map((country) => [foldForComparison(country.name), country.code]));
  }
  return countryIndex.get(folded) ?? null;
}

type AreaEntry = string | readonly [string, ...string[]];

export interface CityInfo {
  name: string;
  country: string;
  aliases: readonly string[];
  areas: readonly AreaEntry[];
}

/** Curated cities for the primary markets; free-text cities are always accepted too. */
export const KNOWN_CITIES: readonly CityInfo[] = [
  {
    name: 'Alexandria',
    country: 'EG',
    aliases: ['alex', 'alexandrie', 'al iskandariyah', 'الاسكندريه', 'اسكندريه'],
    areas: [
      ['Smouha', 'سموحه'], ['Stanley', 'ستانلي'], ['Sidi Gaber', 'سيدي جابر'], ['Miami', 'ميامي'],
      ['Gleem', 'جليم', 'glim'], ['Roushdy', 'رشدي', 'rushdy'], ['Louran', 'لوران', 'laurent'],
      ['Sporting', 'سبورتنج'], ['Cleopatra', 'كليوباترا'], ['Montaza', 'المنتزه'], ['Mandara', 'المندره'],
      ['Agami', 'العجمي'], ['Borg El Arab', 'برج العرب'], ['Mansheya', 'المنشيه'], ['Kafr Abdo', 'كفر عبده'],
      ['Ibrahimia', 'الابراهيميه'], ['Camp Shezar', 'كامب شيزار'], ['Sidi Bishr', 'سيدي بشر'],
      ['Asafra', 'العصافره'], ['Moharam Bek', 'محرم بك'], ['Bolkly', 'بولكلي'], ['Zizinia', 'زيزينيا'],
      ['San Stefano', 'سان ستيفانو'], ['Saba Pasha', 'صبا باشا'], ['Fleming', 'فلمنج'], ['Victoria', 'فيكتوريا'],
      ['Raml Station', 'محطه الرمل'], ['Bahary', 'بحري', 'bahari'], ['Anfoushi', 'الانفوشي'],
      ['Mostafa Kamel', 'مصطفي كامل'], ['Gianaclis', 'جناكليس'], ['Maamoura', 'المعموره'],
      ['Abu Qir', 'ابو قير', 'abu qir'], ['King Mariout', 'كينج مريوط'], ['Amreya', 'العامريه'],
      ['Dekheila', 'الدخيله'], ['Karmouz', 'كرموز'], ['Azarita', 'الازاريطه'], ['Shatby', 'الشاطبي', 'chatby'],
    ],
  },
  {
    name: 'Cairo',
    country: 'EG',
    aliases: ['al qahirah', 'القاهره', 'cairo governorate'],
    areas: [
      ['Maadi', 'المعادي'], ['Zamalek', 'الزمالك'], ['Heliopolis', 'مصر الجديده'], ['Nasr City', 'مدينه نصر'],
      ['New Cairo', 'القاهره الجديده'], ['Fifth Settlement', 'التجمع الخامس', 'tagamoa'], ['Downtown', 'وسط البلد'],
      ['Garden City', 'جاردن سيتي'], ['Shubra', 'شبرا'], ['Abbassia', 'العباسيه'], ['Mokattam', 'المقطم'],
      ['Rehab', 'الرحاب'], ['Madinaty', 'مدينتي'], ['Katameya', 'القطاميه'], ['Helwan', 'حلوان'],
      ['Obour', 'العبور'], ['Shorouk', 'الشروق'],
    ],
  },
  {
    name: 'Giza',
    country: 'EG',
    aliases: ['الجيزه'],
    areas: [
      ['Dokki', 'الدقي'], ['Mohandessin', 'المهندسين'], ['Agouza', 'العجوزه'], ['Haram', 'الهرم'], ['Faisal', 'فيصل'],
      ['6th of October', 'السادس من اكتوبر', '6 october', 'october city'], ['Sheikh Zayed', 'الشيخ زايد'],
      ['Manial', 'المنيل'], ['Imbaba', 'امبابه'],
    ],
  },
  ...(
    [
      ['Mansoura', 'المنصوره'], ['Tanta', 'طنطا'], ['Zagazig', 'الزقازيق'], ['Ismailia', 'الاسماعيليه'],
      ['Port Said', 'بورسعيد'], ['Suez', 'السويس'], ['Hurghada', 'الغردقه'], ['Sharm El Sheikh', 'شرم الشيخ'],
      ['Luxor', 'الاقصر'], ['Aswan', 'اسوان'], ['Asyut', 'اسيوط'], ['Minya', 'المنيا'], ['Damanhur', 'دمنهور'],
      ['Faiyum', 'الفيوم'], ['Beni Suef', 'بني سويف'], ['Sohag', 'سوهاج'], ['Damietta', 'دمياط'],
      ['Kafr El Sheikh', 'كفر الشيخ'], ['Banha', 'بنها'], ['El Alamein', 'العلمين'], ['Marsa Matruh', 'مرسي مطروح'],
    ] as const
  ).map(([name, alias]) => ({ name, country: 'EG', aliases: [alias], areas: [] })),
  {
    name: 'Riyadh',
    country: 'SA',
    aliases: ['الرياض', 'ar riyad'],
    areas: [
      ['Olaya', 'العليا'], ['Al Malqa', 'الملقا'], ['Al Sulaimaniyah', 'السليمانيه'], ['Al Nakheel', 'النخيل'],
      ['Al Yasmin', 'الياسمين'], ['Al Murooj', 'المروج'], ['Al Wurud', 'الورود'], ['Al Aqiq', 'العقيق'],
      ['Hittin', 'حطين'], ['King Abdullah Financial District', 'kafd'], ['Al Malaz', 'الملز'], ['Al Sahafa', 'الصحافه'],
      ['Al Narjis', 'النرجس'], ['Diplomatic Quarter', 'حي السفارات'], ['Qurtubah', 'قرطبه'], ['Al Rabwa', 'الربوه'],
    ],
  },
  {
    name: 'Jeddah',
    country: 'SA',
    aliases: ['jiddah', 'jedda', 'جده'],
    areas: [
      ['Al Rawdah', 'الروضه'], ['Al Zahra', 'الزهراء'], ['Al Hamra', 'الحمراء'], ['Al Andalus', 'الاندلس'],
      ['Al Salamah', 'السلامه'], ['Al Shati', 'الشاطئ'], ['Obhur', 'ابحر'], ['Al Balad', 'البلد'],
      ['Al Khalidiyah', 'الخالديه'], ['Al Naeem', 'النعيم'], ['Al Faisaliyah', 'الفيصليه'], ['Al Basateen', 'البساتين'],
    ],
  },
  {
    name: 'Dammam',
    country: 'SA',
    aliases: ['الدمام'],
    areas: [['Al Faisaliyah', 'الفيصليه'], ['Al Shula', 'الشعله'], ['Al Aziziyah', 'العزيزيه']],
  },
  {
    name: 'Al Khobar',
    country: 'SA',
    aliases: ['khobar', 'الخبر'],
    areas: [['Al Aqrabiyah', 'العقربيه'], ['Al Rakah', 'الراكه'], ['Al Olaya', 'العليا'], ['Al Thuqbah', 'الثقبه']],
  },
  ...(
    [
      ['Mecca', 'makkah', 'مكه'], ['Medina', 'madinah', 'المدينه المنوره'], ['Dhahran', 'dhahran', 'الظهران'],
      ['Taif', 'taif', 'الطائف'], ['Tabuk', 'tabuk', 'تبوك'], ['Buraidah', 'buraydah', 'بريده'], ['Abha', 'abha', 'ابها'],
      ['Khamis Mushait', 'khamis mushayt', 'خميس مشيط'], ['Hail', 'hail', 'حائل'], ['Jazan', 'jizan', 'جازان'],
      ['Najran', 'najran', 'نجران'], ['Al Ahsa', 'hofuf', 'الاحساء'], ['Jubail', 'jubail', 'الجبيل'],
      ['Yanbu', 'yanbu', 'ينبع'], ['Qatif', 'qatif', 'القطيف'],
    ] as const
  ).map(([name, alias1, alias2]) => ({ name, country: 'SA', aliases: [alias1, alias2], areas: [] })),
  {
    name: 'Dubai',
    country: 'AE',
    aliases: ['دبي'],
    areas: [
      ['Business Bay', 'الخليج التجاري'], ['Downtown Dubai', 'وسط مدينه دبي'], ['Jumeirah', 'جميرا'], ['Deira', 'ديره'],
      ['Bur Dubai', 'بر دبي'], ['Dubai Marina', 'دبي مارينا'], ['Jumeirah Lakes Towers', 'jlt'], ['Al Barsha', 'البرشاء'],
      ['DIFC', 'dubai international financial centre'], ['Dubai Media City', 'مدينه دبي للاعلام'],
      ['Dubai Internet City', 'مدينه دبي للانترنت'], ['Al Quoz', 'القوز'], ['Jumeirah Village Circle', 'jvc'],
      ['Dubai Silicon Oasis', 'واحه دبي للسيليكون'], ['Al Karama', 'الكرامه'], ['Al Garhoud', 'القرهود'],
      ['Dubai Design District', 'd3'], ['Mirdif', 'مردف'], ['Al Qusais', 'القصيص'], ['Palm Jumeirah', 'نخله جميرا'],
      ['Umm Suqeim', 'ام سقيم'], ['Al Satwa', 'السطوه'], ['Trade Centre', 'المركز التجاري'], ['Motor City', 'موتور سيتي'],
    ],
  },
  {
    name: 'Abu Dhabi',
    country: 'AE',
    aliases: ['abudhabi', 'ابوظبي', 'ابو ظبي'],
    areas: [
      ['Al Reem Island', 'جزيره الريم'], ['Khalifa City', 'مدينه خليفه'], ['Al Khalidiyah', 'الخالديه'],
      ['Corniche', 'الكورنيش'], ['Al Maryah Island', 'جزيره الماريه'], ['Mussafah', 'مصفح'], ['Yas Island', 'جزيره ياس'],
      ['Saadiyat Island', 'جزيره السعديات'], ['Al Bateen', 'البطين'], ['Masdar City', 'مدينه مصدر'],
      ['Tourist Club Area', 'منطقه النادي السياحي'],
    ],
  },
  {
    name: 'Sharjah',
    country: 'AE',
    aliases: ['الشارقه'],
    areas: [['Al Majaz', 'المجاز'], ['Al Nahda', 'النهده'], ['Al Taawun', 'التعاون'], ['Muwaileh', 'مويلح'], ['Al Khan', 'الخان']],
  },
  ...(
    [
      ['Ajman', 'عجمان'], ['Ras Al Khaimah', 'راس الخيمه'], ['Fujairah', 'الفجيره'], ['Umm Al Quwain', 'ام القيوين'],
      ['Al Ain', 'العين'],
    ] as const
  ).map(([name, alias]) => ({ name, country: 'AE', aliases: [alias], areas: [] })),
  ...(
    [
      ['Kuwait City', 'KW', 'مدينه الكويت'], ['Doha', 'QA', 'الدوحه'], ['Manama', 'BH', 'المنامه'], ['Muscat', 'OM', 'مسقط'],
      ['Amman', 'JO', 'عمان'], ['Beirut', 'LB', 'بيروت'], ['Casablanca', 'MA', 'الدار البيضاء'], ['Rabat', 'MA', 'الرباط'],
      ['Marrakesh', 'MA', 'مراكش'], ['Tunis', 'TN', 'تونس'], ['Istanbul', 'TR', 'اسطنبول'], ['London', 'GB', 'لندن'],
    ] as const
  ).map(([name, country, alias]) => ({ name, country, aliases: [alias], areas: [] })),
];

function entryNames(entry: AreaEntry): readonly string[] {
  return typeof entry === 'string' ? [entry] : entry;
}

function canonicalName(entry: AreaEntry): string {
  return typeof entry === 'string' ? entry : entry[0];
}

export function citiesForCountry(country: string | null | undefined): CityInfo[] {
  if (!country) return [...KNOWN_CITIES];
  const upper = country.toUpperCase();
  return KNOWN_CITIES.filter((city) => city.country === upper);
}

/** Resolve a city name or alias ("Alex", "الإسكندرية") to the curated city, if known. */
export function normalizeCity(input: string | null | undefined, country?: string | null): CityInfo | null {
  if (!input) return null;
  const folded = foldForComparison(input).replace(/\b(city|governorate|emirate|province)\b/g, '').trim();
  if (!folded) return null;
  const candidates = citiesForCountry(country ?? null);
  for (const city of candidates.length > 0 ? candidates : KNOWN_CITIES) {
    if (foldForComparison(city.name) === folded || city.aliases.some((alias) => foldForComparison(alias) === folded)) {
      return city;
    }
  }
  return null;
}

function containsTerm(haystack: string, term: string): boolean {
  const needle = foldForComparison(term);
  if (!needle) return false;
  if (/[؀-ۿ]/.test(needle)) return haystack.includes(needle);
  return new RegExp(`(^|[^\\p{L}\\p{N}])${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^\\p{L}\\p{N}])`, 'u').test(haystack);
}

export interface ParsedLocation {
  city?: string;
  area?: string;
  country?: string;
}

/**
 * Best-effort city/area/country detection inside a free-text address. Only curated names are
 * matched, so the result is conservative: unknown places are left empty instead of guessed.
 */
export function parseLocationFromText(text: string | null | undefined, countryHint?: string | null): ParsedLocation {
  if (!text) return {};
  const haystack = foldForComparison(text);
  const result: ParsedLocation = {};
  const countryMatch = listCountries().find(
    (country) => country.name.length > 3 && containsTerm(haystack, country.name),
  );
  const aliasMatch = Object.entries(COUNTRY_ALIASES).find(([alias]) => alias.length > 2 && containsTerm(haystack, alias));
  const country = countryMatch?.code ?? aliasMatch?.[1] ?? countryHint?.toUpperCase();
  if (country) result.country = country;

  const cities = country ? citiesForCountry(country) : [...KNOWN_CITIES];
  for (const city of cities) {
    if ([city.name, ...city.aliases].some((name) => containsTerm(haystack, name))) {
      result.city = city.name;
      result.country ??= city.country;
      for (const area of city.areas) {
        if (entryNames(area).some((name) => containsTerm(haystack, name))) {
          result.area = canonicalName(area);
          break;
        }
      }
      break;
    }
  }
  return result;
}

/** Look up an area of a known city by name or alias. */
export function normalizeArea(input: string | null | undefined, city: string | null | undefined): string | null {
  if (!input || !city) return null;
  const known = normalizeCity(city);
  if (!known) return null;
  const folded = foldForComparison(input);
  const match = known.areas.find((area) => entryNames(area).some((name) => foldForComparison(name) === folded));
  return match ? canonicalName(match) : null;
}
